"use client"

import { useState, useRef } from "react"
import emailjs from "@emailjs/browser"
import MobileNav from "../../components/MobileNav"
import { MapPin, Mail, Phone, Clock } from "lucide-react"
import "./Contact.css"

export default function Contact() {
  const form = useRef()
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required"
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid"
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required"
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required"
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (validateForm()) {
      setIsSubmitting(true)
      setSubmitStatus(null)

      // Replace these with your actual EmailJS IDs
      const serviceId = "service_ac19unj"
      const templateId = "template_wgi4ode"
      const publicKey = "oWZ1_6NDlNK9PaM_t"

      emailjs
        .sendForm(serviceId, templateId, form.current, publicKey)
        .then((result) => {
          console.log("Email sent successfully:", result.text)
          setIsSubmitting(false)
          setSubmitStatus({
            type: "success",
            message: "Your message has been sent successfully! We will get back to you soon.",
          })
          // Reset form
          setFormData({
            name: "",
            email: "",
            subject: "",
            message: "",
          })
        })
        .catch((error) => {
          console.error("Failed to send email:", error.text)
          setIsSubmitting(false)
          setSubmitStatus({
            type: "error",
            message: "Failed to send your message. Please try again later or contact us directly at flv10ius@gmail.com",
          })
        })
    }
  }

  return (
    <>
      <header className="light-header">
        <div className="container">
          <div className="logo">
            <a
              href="/"
              style={{
                textDecoration: "none",
                fontWeight: "800",
                fontSize: "1.8rem",
                color: "#2c3e50",
              }}
            >
              Uniquely<span style={{ color: "#c0392b" }}>Sharp</span>
            </a>
          </div>
          <nav className="desktop-nav">
            <a href="/">Home</a>
            <a href="/events">Events</a>
            <a href="/products">Products</a>
            <a href="/about">About Us</a>
            <a className="active" href="/contact">
              Contact
            </a>
          </nav>
          <MobileNav />
        </div>
      </header>

      <div className="hero-section" style={{ backgroundImage: `url('https://i.imgur.com/9ZjBPNq.jpeg')` }}>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Contact Us</h1>
          <p>We'd love to hear from you</p>
        </div>
      </div>

      <section className="contact-section">
        <div className="container">
          <div className="contact-container">
            <div className="contact-info">
              <h2 className="section-title">Get In Touch</h2>
              <p>
                Have questions about our products or services? We're here to help! Fill
                out the form and our team will get back to you as soon as possible.
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="contact-text">
                    <h3>Our Location</h3>
                    <p>
                    Bettendorf, Iowa
                    </p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <Mail size={24} />
                  </div>
                  <div className="contact-text">
                    <h3>Email Us</h3>
                    <p>
                    UniquelySharpContact@gmail.com
                    </p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <div className="contact-text">
                    <h3>Call Us</h3>
                    <p>
                      (+1) 563-508-2716
                    </p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <Clock size={24} />
                  </div>
                  <div className="contact-text">
                    <h3>Business Hours</h3>
                    <p>
                      Monday - Sunday: 9am - 5pm
                    </p>
                  </div>
                </div>
              </div>

              <div className="social-connect">
                <h3>Connect With Us</h3>
                <div className="social-icons">
                  <a href="https://www.facebook.com/share/g/15nBKAPV6a/?mibextid=wwXIfr" aria-label="Facebook">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                    </svg>
                  </a>
                  <a href="https://www.instagram.com/uniquely_sharp" aria-label="Instagram">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <h2 className="form-title">Send Us a Message</h2>

              {submitStatus && <div className={`submit-message ${submitStatus.type}`}>{submitStatus.message}</div>}

              <form ref={form} className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? "error" : ""}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Your Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? "error" : ""}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={errors.subject ? "error" : ""}
                  />
                  {errors.subject && <span className="error-message">{errors.subject}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message">Your Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    className={errors.message ? "error" : ""}
                  ></textarea>
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>

                <button type="submit" className="submit-button" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>

          <div className="map-container">
            <h2 className="section-title">Find Us</h2>
            <div className="map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47763.05027278595!2d-90.51997831646703!3d41.56511841650078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87e237a8015d0c69%3A0x3544de99b2fafbc!2sBettendorf%2C%20IA!5e0!3m2!1sen!2sus!4v1744424011111!5m2!1sen!2sus"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Our Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer-section">
        <div className="footer-content">
          <div className="footer-column">
            <h3>
              Uniquely<span>Sharp</span>
            </h3>
            <p>Premium quality knives and cutting tools for professionals and enthusiasts.</p>
            <div className="social-icons">
              <a href="https://www.facebook.com/share/g/15nBKAPV6a/?mibextid=wwXIfr" aria-label="Facebook">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href="https://www.instagram.com/uniquely_sharp" aria-label="Instagram">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
            </div>
          </div>

          <div className="footer-column">
            <h3>Company</h3>
            <ul>
              <li>
                <a href="/">Home</a>
              </li>
              <li>
                <a href="/events">Events</a>
              </li>
              <li>
                <a href="/products">Products</a>
              </li>
              <li>
                <a href="/about">About Us</a>
              </li>
              <li>
                <a href="/contact">Contact</a>
              </li>
            </ul>
          </div>

          <div className="footer-column">
            <h3>Contact</h3>
            <p>
            Bettendorf, Iowa
            </p>
            <p>
            UniquelySharpContact@gmail.com
              <br />
              (+1) 563-508-2716
            </p>
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            © 2025 <strong>Uniquely Sharp</strong>. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  )
}
